/**
 * Relative paths appended to the HTTP client base (`…/api/`).
 * Keep contract paths in one place for safer refactors.
 */
export const API_PATHS = {
  /**
   * Legacy billing preview — still accepts browser `restaurant_id` (strangler debt).
   * Live storefront must use {@link API_PATHS.storefrontBillingPreview}.
   */
  BILLING_PREVIEW: "billing/v1/preview",
  ORDER_CHECKOUT: "orders/v1/order-checkout",
  PAYMENT_CHECKOUT: "Payment/v1/checkout",
  /** PUT — customer-authenticated cancel of unpaid order. */
  customerCancel: (orderId: string) =>
    `orders/v1/${encodeURIComponent(orderId)}/customer-cancel`,
  /*
   * `customerOrder` (GET orders/v1/customer/{orderId}) was removed with its endpoint. Customer
   * order detail is tenant-scoped now — see buildPublicCustomerOrderDetailPath in
   * services/public-storefront-paths.ts.
   */
  /**
   * POST / DELETE — customer browser registration for order-status Web Push.
   * Nested under `orders/v1` because the Vercel adapter forwards only allowlisted
   * prefixes (`/api/orders/` among them); a new top-level prefix would 404 in production.
   */
  CUSTOMER_PUSH_SUBSCRIPTIONS: "orders/v1/customer/push-subscriptions",
  /**
   * POST — the signed-in customer asks for their own account to be deleted (202 Accepted).
   * GET — the customer's latest deletion request (404 when none). The account is bound from the
   * JWT only; never the admin `users/v1/{user_id}/deletion-request` routes.
   */
  MY_DELETION_REQUEST: "users/v1/me/deletion-request",

  /**
   * DELETE — the signed-in customer removes one of their own saved addresses.
   * Soft delete server-side: the address disappears from every owner read, and past orders
   * that reference it are unaffected. 404 means the address is already gone.
   */
  customerAddress: (addressId: string) =>
    `user/addresses/v1/${encodeURIComponent(addressId.trim())}`,

  /**
   * Phase 1B.4-IIIA.1 — PLATFORM_SLUG public storefront resources.
   * Route value `{slug}` is for middleware tenant identity — never RestaurantId.
   */
  storefrontRestaurant: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/restaurant`,
  storefrontMenu: (slug: string, orderChannel: number) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/menu?orderChannel=${orderChannel}`,
  storefrontDiscounts: (slug: string, orderChannel: number) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/discounts?orderChannel=${orderChannel}`,
  storefrontCompliancePreview: (slug: string, menuItemId: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/menu-items/${encodeURIComponent(menuItemId.trim())}/compliance/preview`,
  /** Phase 1B.4-IIIB-1 — public billing preview (tenant from route slug / middleware). */
  storefrontBillingPreview: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/billing/preview`,
  /** Phase 1B.4-IIIB-2 — public order checkout (tenant from route slug / middleware). */
  storefrontOrderCheckout: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/orders/checkout`,
  /** Slice 8B — public serviceability pre-check (tenant from route slug / middleware). */
  storefrontServiceabilityCheck: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/serviceability/check`,
  /** Phase 1 current-location resolve (tenant from route slug / middleware). Authenticated. */
  storefrontLocationResolve: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/location/resolve`,
  /** Phase P6.1 — public homepage composition. */
  storefrontHomepage: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/homepage`,
  /** Phase 2 — public online-ordering availability projection (anonymous, read-only). */
  storefrontOrderingStatus: (slug: string) =>
    `public/v1/storefront/r/${encodeURIComponent(slug.trim())}/ordering/status`,
} as const

export type ApiPathKey = keyof typeof API_PATHS
